import { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, WMSTileLayer, useMapEvents, useMap } from 'react-leaflet';
import { Layers, Info, MapPin, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { PageHeader } from '../components/layout/PageHeader';
import { GeolocationButton } from '../components/GeolocationButton';
import { API_BASE_URL } from '../config/api';

// WMS del Gobierno de Canarias - Mapa de Cultivos
const WMS_URL = 'https://idecan2.grafcan.es/ServicioWMS/Cultivos';
const WMS_LAYER = 'MapaCultivos';

// Proxy URL para evitar CORS
const PROXY_URL = `${API_BASE_URL}/api/ckan-proxy`;

// Componente para manejar clicks en el mapa y obtener info de cultivos
function MapClickHandler({ onFeatureInfo, wmsEnabled, setLoading }) {
  const map = useMap();
  
  useMapEvents({
    click: async (e) => {
      if (!wmsEnabled) return;
      
      setLoading(true);
      const { lat, lng } = e.latlng;
      const size = map.getSize();
      const bounds = map.getBounds();
      
      // Construir URL GetFeatureInfo
      const bbox = `${bounds.getSouth()},${bounds.getWest()},${bounds.getNorth()},${bounds.getEast()}`;
      const point = map.latLngToContainerPoint(e.latlng);
      
      const wmsParams = new URLSearchParams({
        SERVICE: 'WMS',
        VERSION: '1.3.0',
        REQUEST: 'GetFeatureInfo',
        LAYERS: WMS_LAYER,
        QUERY_LAYERS: WMS_LAYER,
        INFO_FORMAT: 'text/html',
        CRS: 'EPSG:4326',
        BBOX: bbox,
        WIDTH: size.x,
        HEIGHT: size.y,
        I: Math.round(point.x),
        J: Math.round(point.y),
      }).toString();
      
      const fullWmsUrl = `${WMS_URL}?${wmsParams}`;
      
      try {
        // Usar el proxy del backend para evitar CORS
        const proxyUrl = `${PROXY_URL}?url=${encodeURIComponent(fullWmsUrl)}&format=text`;
        const response = await fetch(proxyUrl);
        const html = await response.text();
        
        // Parsear la respuesta HTML para extraer atributos
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Buscar las celdas con clase 'epigrafe' y 'valor'
        const attributes = {};
        const epigrafes = doc.querySelectorAll('.epigrafe');
        
        epigrafes.forEach(epigrafe => {
          const key = epigrafe.textContent?.trim().replace(/:$/, '');
          const valorCell = epigrafe.closest('tr')?.querySelector('.valor');
          const value = valorCell?.textContent?.trim();
          
          if (key && value && key !== value) {
            attributes[key] = value;
          }
        });
        
        // Fallback: buscar tablas con datos
        if (Object.keys(attributes).length === 0) {
          const tables = doc.querySelectorAll('table');
          tables.forEach(table => {
            const rows = table.querySelectorAll('tr');
            rows.forEach(row => {
              const cells = row.querySelectorAll('td, th');
              if (cells.length >= 2) {
                const key = cells[0].textContent?.trim().replace(/:$/, '');
                const value = cells[1].textContent?.trim();
                if (key && value && key !== value && !key.includes('http')) {
                  attributes[key] = value;
                }
              }
            });
          });
        }
        
        if (Object.keys(attributes).length > 0) {
          onFeatureInfo({
            latlng: e.latlng,
            attributes
          });
        } else {
          onFeatureInfo(null);
        }
      } catch (error) {
        console.error('Error en GetFeatureInfo:', error);
        onFeatureInfo(null);
      } finally {
        setLoading(false);
      }
    }
  });
  
  return null;
}

export function MapaCultivosPage() {
  const [wmsEnabled, setWmsEnabled] = useState(true);
  const [featureInfo, setFeatureInfo] = useState(null);
  const [opacity, setOpacity] = useState(0.6);
  const [loadingInfo, setLoadingInfo] = useState(false);

  function handleFeatureInfo(info) {
    setFeatureInfo(info);
  }

  function clearFeatureInfo() {
    setFeatureInfo(null);
  }

  return (
    <div className="px-4 py-4" data-testid="mapa-cultivos-page">
      <PageHeader 
        title="Mapa de Cultivos" 
        subtitle="Datos oficiales del Gobierno de Canarias"
        showBack
      />

      {/* Controles */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Switch
            id="wms-toggle"
            checked={wmsEnabled}
            onCheckedChange={setWmsEnabled}
            data-testid="toggle-wms"
          />
          <Label htmlFor="wms-toggle" className="text-sm">
            Mostrar cultivos
          </Label>
        </div>
        
        <Badge variant="outline" className="text-xs">
          <Layers size={12} className="mr-1" />
          WMS IDECanarias
        </Badge>
      </div>

      {/* Instrucciones */}
      <div className="mt-3 text-sm text-muted-foreground flex items-center gap-2">
        <Info size={14} />
        <span>Pulsa sobre un área coloreada para ver el tipo de cultivo</span>
      </div>

      {/* Mapa */}
      <div className="mt-4 h-[500px] rounded-xl overflow-hidden border border-border relative">
        <MapContainer 
          center={[28.35, -16.5]} 
          zoom={10} 
          className="h-full w-full"
          scrollWheelZoom={true}
        >
          {/* Capa base */}
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* Capa WMS de Cultivos */}
          {wmsEnabled && (
            <WMSTileLayer
              url={WMS_URL}
              layers={WMS_LAYER}
              format="image/png"
              transparent={true}
              opacity={opacity}
              version="1.3.0"
              crs={L.CRS.EPSG4326}
              attribution='Mapa de Cultivos: Gobierno de Canarias'
            />
          )}
          
          <GeolocationButton />
          <MapClickHandler 
            onFeatureInfo={handleFeatureInfo} 
            wmsEnabled={wmsEnabled}
            setLoading={setLoadingInfo}
          />
        </MapContainer>
        
        {/* Indicador de carga */}
        {loadingInfo && (
          <div className="absolute top-4 right-4 z-[1000]">
            <Badge variant="secondary" className="animate-pulse">
              Consultando...
            </Badge>
          </div>
        )}
        
        {/* Panel de información del cultivo */}
        {featureInfo && (
          <div className="absolute bottom-4 left-4 right-4 z-[1000]">
            <Card className="bg-white/95 backdrop-blur shadow-lg">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <MapPin size={16} className="text-primary" />
                  Información del cultivo
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6"
                  onClick={clearFeatureInfo}
                >
                  <X size={14} />
                </Button>
              </CardHeader>
              <CardContent className="pt-0">
                <ScrollArea className="max-h-[200px]">
                  <div className="space-y-1 text-sm">
                    {Object.entries(featureInfo.attributes)
                      .filter(([key]) => {
                        const keyLower = key.toLowerCase();
                        // Mostrar campos relevantes de cultivos
                        return keyLower.includes('cultivo') || 
                               keyLower.includes('categor') || 
                               keyLower.includes('regad') ||
                               keyLower.includes('técnica') ||
                               keyLower.includes('tecnica') ||
                               keyLower.includes('abandono') ||
                               keyLower.includes('fecha') ||
                               keyLower.includes('diseminado') ||
                               keyLower.includes('borde');
                      })
                      .map(([key, value]) => {
                        const cleanKey = key.replace(/:+$/, '').trim();
                        const cleanValue = value.replace(/^:+/, '').trim();
                        return (
                          <div key={key} className="flex gap-2">
                            <span className="font-medium text-muted-foreground min-w-[140px]">{cleanKey}:</span>
                            <span>{cleanValue}</span>
                          </div>
                        );
                      })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Control de opacidad */}
      <div className="mt-4 flex items-center gap-4">
        <Label className="text-sm text-muted-foreground">Opacidad:</Label>
        <input
          type="range"
          min="0.2"
          max="1"
          step="0.1"
          value={opacity}
          onChange={(e) => setOpacity(parseFloat(e.target.value))}
          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          disabled={!wmsEnabled}
        />
        <span className="text-sm text-muted-foreground w-12">{Math.round(opacity * 100)}%</span>
      </div>

      {/* Leyenda */}
      <Card className="mt-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Leyenda</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#7CFC00' }}></div>
              <span>Cultivos herbáceos</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#228B22' }}></div>
              <span>Frutales</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#FFD700' }}></div>
              <span>Platanera</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#8B4513' }}></div>
              <span>Viña</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#FF6347' }}></div>
              <span>Tomate</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#808080' }}></div>
              <span>Abandonado</span>
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground italic">
            Los colores son aproximados. Consulte la leyenda oficial del servicio WMS.
          </p>
        </CardContent>
      </Card>

      {/* Fuente de datos */}
      <div className="mt-4 p-3 bg-muted/50 rounded-lg text-center text-xs text-muted-foreground">
        <strong>Fuente:</strong> Gobierno de Canarias – Mapa de Cultivos (datos abiertos, WMS SITCAN/IDECanarias)
        <br />
        <a 
          href="https://www.idecanarias.es/listado_servicios/mapa-de-cultivos" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          Más información sobre el servicio
        </a>
      </div>
    </div>
  );
}

export default MapaCultivosPage;
